import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { WorkOrder, Equipment } from '@/types'
import { isOverdue } from '@/lib/utils'

interface ActiveWorkOrdersCardProps {
  workOrders: WorkOrder[]
  isLoading: boolean
  onViewAll: () => void
  equipment?: Equipment[]
}

export default function ActiveWorkOrdersCard({ workOrders, isLoading, onViewAll, equipment = [] }: ActiveWorkOrdersCardProps) {
  // Helper function to get equipment details by ID
  const getEquipmentById = (equipmentId: string) => {
    return equipment.find(eq => eq.id === equipmentId)
  }

  const getStatusVariant = (status: WorkOrder['status'], dueDate: string): 'critical' | 'warning' | 'info' | 'success' | 'default' => {
    if (isOverdue(dueDate)) return 'critical'
    
    switch (status) {
      case 'in-progress': return 'info'
      case 'assigned': return 'warning'
      case 'pending': return 'warning'
      case 'completed': return 'success'
      case 'on-hold': return 'default'
      default: return 'default'
    }
  }

  const getStatusDisplay = (status: WorkOrder['status'], dueDate: string) => {
    if (isOverdue(dueDate)) return 'Overdue'
    
    switch (status) {
      case 'in-progress': return 'In Progress'
      case 'assigned': return 'Assigned'
      case 'pending': return 'Pending'
      case 'completed': return 'Completed'
      case 'on-hold': return 'On Hold'
      case 'cancelled': return 'Cancelled'
      default: return status
    }
  }

  const getDueDateDisplay = (dueDate: string) => {
    const due = new Date(dueDate)
    const now = new Date()
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} overdue`
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return 'Due tomorrow'
    return `Due in ${diffDays} days`
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Active Work Orders
            <Button variant="ghost" size="sm" disabled>View All</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg animate-pulse">
                <div className="space-y-1 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="text-right space-y-1">
                  <div className="w-16 h-6 bg-gray-200 rounded"></div>
                  <div className="w-12 h-3 bg-gray-200 rounded ml-auto"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Active Work Orders
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View All
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {workOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No active work orders</p>
          </div>
        ) : (
          <div className="space-y-4">
            {workOrders.slice(0, 5).map((workOrder) => (
              <div key={workOrder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{workOrder.title}</p>
                  <p className="text-sm text-gray-600 truncate">
                    {workOrder.equipment?.name || getEquipmentById(workOrder.equipmentId)?.name || 'Unknown Equipment'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {workOrder.assignedTo ? `Assigned to ${workOrder.assignedTo.name}` : 'Unassigned'}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <Badge variant={getStatusVariant(workOrder.status, workOrder.dueDate)}>
                    {getStatusDisplay(workOrder.status, workOrder.dueDate)}
                  </Badge>
                  <p className="text-xs text-gray-500 mt-1">
                    {getDueDateDisplay(workOrder.dueDate)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}